import { useState, useEffect } from "react";
import { Image } from "@/components/ui/image";

import { base44 } from "@/api/base44Client";
import { useSaved } from "@/lib/SavedContext";
import { Map, Marker } from "@vis.gl/react-maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import SocialBar from "@/components/yts/SocialBar";
import LoopExperienceShowcase from "@/components/yts/LoopExperienceShowcase";
import ListingEditModal from "@/components/yts/ListingEditModal";
import PerfumeriaPRShop from "@/pages/PerfumeriaPRShop";
import BusinessNotFound from "@/components/yts/BusinessNotFound";
import ClaimBusinessButton from "@/components/yts/business/ClaimBusinessButton";
import VerifiedBadge from "@/components/yts/business/VerifiedBadge";
import ShareButton from "@/components/yts/ShareButton";
import BusinessQrCard from "@/components/yts/BusinessQrCard";
import { businessLink } from "@/lib/businessSlug";
import { useLang } from "@/lib/i18n";
import { trackGuideAction } from "@/lib/guideJourney";
import { categoryLabel, neighborhoodLabel, actionLabel, listingNameLabel } from "@/lib/locales";
import PlacePin from "@/components/yts/PlacePin";
import KaraokeEventFeature from "@/components/yts/KaraokeEventFeature";
import AdSlot from "@/components/yts/AdSlot";
import BeachCardDetails from "@/components/yts/BeachCardDetails";
import BookingPartnerLogo from "@/components/yts/BookingPartnerLogo";
import { listingToBeach } from "@/data/beaches";
import { MAP_STYLE, MAP_STYLE_DARK } from "@/lib/mapConfig";
import { useTheme } from "@/lib/theme";
import OpenStatusLine from "@/components/yts/hours/OpenStatusLine";
import DetailHero from "@/components/yts/business/DetailHero";
import DetailSummary from "@/components/yts/business/DetailSummary";
import DetailTabs from "@/components/yts/business/DetailTabs";
import DetailHours from "@/components/yts/business/DetailHours";
import DetailContact from "@/components/yts/business/DetailContact";
import DetailAbout from "@/components/yts/business/DetailAbout";
import BusinessPhotoStory from "@/components/yts/business/BusinessPhotoStory";
import DetailTips from "@/components/yts/business/DetailTips";
import CommunityHeartButton from "@/components/yts/CommunityHeartButton";
import { parseWeek } from "@/lib/hours";
import { Calendar, ChevronLeft, ChevronRight, Pencil, X } from "lucide-react";

// A quiet rule between sections keeps the page's rhythm even without boxes.
const SECTION = "mt-6 border-t border-[#E7E5DF] pt-5 dark:border-white/[0.09]";

// A field counts as unpopulated when it is missing or holds nothing but
// spaces — either way it prints no row at all.
const filled = (value) => typeof value === "string" ? value.trim().length > 0 : !!value;

const withProtocol = (url) => (url && !/^https?:\/\//i.test(url) ? "https://" + url : url);

/**
 * The business detail body itself — no page chrome, no route. Used by the
 * standalone business page (deep links, sharing) and by the in-page detail
 * overlay opened from cards.
 */
export default function ListingDetailContent({ id, hideMap = false }) {
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isSaved, toggleSave } = useSaved();
  const [form, setForm] = useState({ author_name: "", body: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const isAdmin = me?.role === "admin";
  const { t, lang } = useLang();
  const { isDark } = useTheme();
  const es = lang === "es";

  const loadReviews = () => base44.entities.Review.filter({ listing_id: id }).then(setReviews).catch(() => {});

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        setListing(await base44.entities.Listing.get(id));
      } catch {


        // not found
      } finally {setLoading(false);}
    })();
    loadReviews();
    base44.auth.me().then(setMe).catch(() => {});
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || !form.body.trim()) return;
    setSubmitting(true);
    try {
      await base44.entities.Review.create({
        listing_id: id,
        author_name: form.author_name,
        body: form.body
      });
      setForm({ author_name: "", body: "" });
      loadReviews();
    } catch (err) {
      alert(err.message || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
  return (
    <div className="grid place-items-center py-24">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>);


  if (!listing) return <BusinessNotFound />;
  // Perfumeria PR is a real mini-storefront with a live catalog and pickup bag,
  // so its directory card should open the shop rather than the generic profile.
  if (listing.slug === "perfumeria-pr" || listing.link_tag === "perfumeria-pr") {
    return <PerfumeriaPRShop embedded />;
  }

  // Spanish mode never silently falls back to English editorial copy. Records
  // without a reviewed translation use a clear Spanish placeholder until their
  // bilingual content is completed.
  const profileCategory = categoryLabel(listing.subcategory || listing.category || listing.type || (es ? "negocio local" : "local business"), lang);
  const profileArea = neighborhoodLabel(listing.neighborhood || listing.city || listing.beach_area || "Puerto Rico", lang);
  // Display-only copy makes sparse profiles feel complete without inventing specific business claims.
  const generatedDescription = es
    ? `Explora ${listingNameLabel(listing, lang)}, una opción de ${profileCategory.toLowerCase()} en ${profileArea}, Puerto Rico. Consulta los detalles disponibles, la ubicación y las formas de conectar antes de visitar o reservar.`
    : `Explore ${listingNameLabel(listing, lang)}, a ${profileCategory.toLowerCase()} in ${profileArea}, Puerto Rico. Review the available details, location and ways to connect before visiting or booking.`;
  const editorialDescriptionText = es ? (listing.editorial_description_es || "") : listing.editorial_description;
  const taglineText = es ? (listing.tagline_es || "") : listing.tagline;

  // Basic profiles get one strong lead image. Premium unlocks the richer photo
  // gallery while every tier keeps the business identity and essential details.
  const isLoopShowcase = /loop experience/i.test(listing.name || "");
  const isListingPremium = isLoopShowcase || (listing.plan === "Premium" && (!listing.plan_until || new Date(listing.plan_until).getTime() > Date.now()));
  const allListingPhotos = Array.from(
    new Set([listing.storefront_image, listing.cover_image, ...(Array.isArray(listing.gallery_images) ? listing.gallery_images : [])].filter((src) => typeof src === "string" && src.trim()))
  );
  // Display every photo already saved to the listing, regardless of plan.
  // Plan limits belong in the owner upload flow; visitors should be able to browse
  // the complete published gallery.
  const gallery = allListingPhotos;
  const saved = isSaved(listing.id);
  // A beach is an ordinary listing record; the guide reads its own fields off it.
  const isBeach = listing.type === "Beach";
  const beach = isBeach ? listingToBeach(listing) : null;
  const hasCoords = listing.latitude != null && listing.longitude != null;
  const profileName = listingNameLabel(listing, lang);
  const gmapsDir = hasCoords ?
  `https://www.google.com/maps/dir/?api=1&destination=${listing.latitude},${listing.longitude}` :
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.address || listing.name)}`;
  const wazeDir = hasCoords ?
  `https://waze.com/ul?ll=${listing.latitude},${listing.longitude}&navigate=yes` :
  `https://waze.com/ul?q=${encodeURIComponent(listing.address || listing.name)}`;
  const isRestaurant = listing.type !== "Experience" && /restaurant|food|drink|bar|cafe|coffee|dining/i.test(`${listing.type || ""} ${listing.category || ""} ${listing.subcategory || ""}`);
  const areaText = neighborhoodLabel(listing.neighborhood || listing.city || listing.beach_area || "", lang);
  const hasContact = filled(listing.address) || filled(listing.phone) || filled(listing.website_url) || filled(areaText) || filled(listing.language_support);
  const menuUrl = withProtocol(listing.website_url || listing.primary_action_url || listing.booking_url || listing.whatsapp_url || "");
  const hasHours = Boolean(parseWeek(listing.hours_json) || String(listing.hours_text || "").trim());
  // A place that has not opened yet keeps the profile written for it: the flag
  // then reads as a notice above the page rather than replacing a real profile
  // with the claim invitation an empty service page gets.
  const openingSoon = Boolean(listing.coming_soon);
  const hasOwnProfile =
    gallery.length > 0 ||
    [listing.description, listing.description_es, listing.editorial_description, listing.editorial_description_es].some((text) => filled(text));
  const placeholdersOnly = openingSoon && !hasOwnProfile;
  const descriptionText = es
    ? (listing.description_es || (placeholdersOnly ? `Estamos reuniendo opciones de ${profileCategory.toLowerCase()} en Puerto Rico. Si administras este negocio, reclama este espacio para añadir información y aparecer en el mapa.` : listing.description || generatedDescription))
    : (listing.description || generatedDescription);

  // The few facts worth scanning at a glance, right under the name.
  const priceText = listing.price_range || listing.price_level || listing.price_label || "";
  const summaryTags = [priceText, ...(Array.isArray(listing.amenities) ? listing.amenities : [])]
    .filter((tag) => filled(tag))
    .slice(0, 3);

  // Only render navigation for sections that will actually exist on this profile.
  const navItems = [
    ...(!placeholdersOnly && (editorialDescriptionText || descriptionText)
      ? [{ id: "about", label: es ? "Acerca de" : "About" }]
      : []),
    ...(!placeholdersOnly && isRestaurant
      ? [{ id: "offerings", label: es ? "Menú" : "Menu" }]
      : []),
    ...(!placeholdersOnly && gallery.length > 1
      ? [{ id: "photos", label: es ? "Fotos" : "Photos" }]
      : []),
    ...(hasCoords && !hideMap
      ? [{ id: "location", label: es ? "Ubicación" : "Location" }]
      : []),
    ...(!placeholdersOnly
      ? [{ id: "reviews", label: es ? "Consejos" : "Tips" }]
      : []),
  ];

  const openExternal = (url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      const category = /fareharbor|viator|getyourguide|booking|reserve/i.test(url) ? "guide_booking_click" : /maps|waze/i.test(url) ? "guide_directions_click" : "guide_business_outbound_click";
      trackGuideAction(category, { origin: "listing" });
    }
    if (url.startsWith("tel:")) {
      window.location.href = url;
      return;
    }
    // Booking providers, maps and business websites commonly block iframe
    // embedding. Open the visitor's chosen action directly in a new tab so
    // the conversion path works reliably on mobile and desktop.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Match the primary action to what a person is actually trying to do.
  // Explicit custom actions are respected except where the listing's intent is
  // unambiguous (appointments, experiences, dining, and ordering).
  const cta = (() => {
    const kind = (listing.type || "").toLowerCase();
    const taxonomy = `${listing.category || ""} ${listing.subcategory || ""} ${listing.name || ""}`.toLowerCase();
    const bookingUrl = listing.primary_action_url || listing.booking_url || listing.website_url || listing.whatsapp_url;
    const siteUrl = listing.website_url || listing.booking_url || listing.primary_action_url;
    const explicitAppointmentAction = /appointment|book/.test(listing.primary_action_type || "");
    const appointmentUrl = listing.booking_url || (explicitAppointmentAction ? listing.primary_action_url : null);
    const isAppointmentService = /barber|barbershop|salon|beauty|nail|nails|spa|massage|tattoo|piercing|grooming|haircut|hair stylist/.test(taxonomy);

    const chosenAction = (listing.primary_action_type || "").toLowerCase();
    const contactUrl = listing.whatsapp_url || siteUrl;
    if (kind === "experience") {
      if (chosenAction === "contact business") return { label: "Contact Business", url: contactUrl };
      if (chosenAction === "get tickets") return { label: "Get Tickets", url: bookingUrl };
      return { label: "Book Now", url: bookingUrl };
    }
    if (isAppointmentService) return appointmentUrl
      ? { label: "Book Appointment", url: appointmentUrl }
      : { label: "Contact Business", url: contactUrl };
    if (kind === "restaurant") {
      if (chosenAction === "view menu" && listing.primary_action_url) return { label: "View Menu", url: listing.primary_action_url };
      if (chosenAction === "contact business") return { label: "Contact Business", url: contactUrl };
      if (listing.booking_url || /reserve table|reservation/.test(chosenAction))
        return { label: "Reserve Table", url: bookingUrl };
      if (chosenAction === "order online" || /order online|takeout|take-out|delivery/.test(taxonomy))
        return { label: "Order Online", url: bookingUrl };
      return { label: "View Menu", url: siteUrl };
    }
    if (kind === "nightlife") {
      if (chosenAction === "contact business") return { label: "Contact Business", url: contactUrl };
      if (chosenAction === "get tickets") return { label: "Get Tickets", url: bookingUrl };
      if (listing.booking_url || /reserve table|reservation/.test(chosenAction))
        return { label: "Reserve Table", url: bookingUrl };
      return { label: "View Menu", url: siteUrl };
    }
    if (kind === "shop") {
      if (chosenAction === "contact business") return { label: "Contact Business", url: contactUrl };
      if (chosenAction === "shop online" || listing.booking_url || chosenAction === "order online") return { label: "Shop Online", url: bookingUrl };
      return { label: "Visit Website", url: siteUrl };
    }
    if (kind === "service") {
      if (chosenAction === "contact business") return { label: "Contact Business", url: contactUrl };
      if (appointmentUrl) return { label: "Book Appointment", url: appointmentUrl };
      return { label: "Contact Business", url: contactUrl };
    }
    if (kind === "culture" && (listing.booking_url || listing.primary_action_url)) {
      if (chosenAction === "visit website") return { label: "Visit Website", url: siteUrl };
      if (chosenAction === "contact business") return { label: "Contact Business", url: contactUrl };
      return { label: "Get Tickets", url: bookingUrl };
    }
    if (chosenAction === "contact business") return { label: "Contact Business", url: contactUrl };
    if (chosenAction === "get directions") return { label: "Get Directions", url: gmapsDir };
    if (listing.primary_action_type) return { label: listing.primary_action_type, url: bookingUrl };
    return { label: "Visit Website", url: siteUrl };
  })();
  // Every listing gets the strongest real action supported by its saved contact data.
  // Never invent booking destinations: fall back to the official website, WhatsApp, or phone.
  const publicCta = cta.url
    ? cta
    : listing.whatsapp_url
      ? { label: "Contact Business", url: listing.whatsapp_url }
      : listing.phone
        ? { label: "Call Business", url: `tel:${listing.phone}` }
        : { label: "Get Directions", url: gmapsDir };
  const bookingActionLabel = actionLabel(publicCta.label, lang);
  // FareHarbor booking URLs are intercepted by the global Auto-Lightframe script.
  // They must be ordinary same-page links, not window.open() calls or new-tab buttons.
  const fareHarborUrl = listing.affiliate_provider === "FareHarbor" &&
    /^https:\/\/fareharbor\.com\/embeds\/book\//i.test(String(listing.booking_url || listing.primary_action_url || ""))
    ? (listing.booking_url || listing.primary_action_url)
    : "";
  const BookingActionTag = fareHarborUrl && publicCta.url === fareHarborUrl ? "a" : "button";

  const statusLine = (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {hasHours ? (
        <button
          type="button"
          onClick={() => document.getElementById("hours")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="inline-flex min-w-0 items-center gap-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717] dark:focus-visible:ring-white"
        >
          <span className="text-[11px] font-bold uppercase tracking-[0.06em]"><OpenStatusLine listing={listing} /></span>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#8d8d88]" />
        </button>
      ) : (
        <OpenStatusLine listing={listing} />
      )}
      {listing.verified && isListingPremium && <VerifiedBadge size="sm" />}
      {!placeholdersOnly && !openingSoon && <CommunityHeartButton listingId={listing.id} compact />}
    </div>
  );

  const mapNode = hasCoords && !hideMap ? (
    <Map
      initialViewState={{ longitude: listing.longitude, latitude: listing.latitude, zoom: 14 }}
      mapStyle={isDark ? MAP_STYLE_DARK : MAP_STYLE}
      attributionControl={false}
      style={{ height: "100%" }}>
      <Marker longitude={listing.longitude} latitude={listing.latitude} anchor="bottom">
        <PlacePin listing={listing} size={26} />
      </Marker>
    </Map>
  ) : null;

  return (
    <div className="mx-auto max-w-[760px] px-4 pb-28 pt-3 text-[#20211f] dark:text-[#f4f2ed] sm:px-6 sm:pb-16 sm:pt-5">
      {/* A place that has not opened yet says so first, then its own profile
            below reads as any other. */}
      {openingSoon && hasOwnProfile && (
        <div className="mb-3 flex items-start gap-3 rounded-[12px] border border-[#cfcfca] bg-[#EDEBE6] px-3.5 py-3 dark:border-white/[0.14] dark:bg-white/[0.06]">
          <span aria-hidden="true" className="mt-[5px] h-2 w-2 shrink-0 rounded-full bg-[#c62e2a]" />
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[#6d6d6a] dark:text-[#aca89f]">{t("detail.comingSoon")}</div>
            <p className="mt-1 text-[13px] font-medium leading-[1.5] text-[#20211f] dark:text-[#f4f2ed]">
              {es ? "Todavía no está abierto. Muy pronto." : "Not open yet. Opening soon."}
            </p>
          </div>
        </div>
      )}

      {!placeholdersOnly && (
        <section id="photos" className="scroll-mt-24">
          <DetailHero
            photos={gallery}
            title={profileName}
            category={categoryLabel(listing.subcategory || listing.category || listing.type || "", lang)}
            saved={saved}
            onToggleSave={() => toggleSave(listing.id)}
            onOpen={setLightbox}
            saveLabel={t("detail.save")}
          />
        </section>
      )}

      {!placeholdersOnly && (
        <DetailSummary
          title={profileName}
          status={statusLine}
          address={filled(listing.address) ? listing.address : areaText}
          tags={summaryTags}
          description={taglineText || descriptionText}
          onDirections={() => openExternal(gmapsDir)}
          directionsLabel={t("detail.directions")}
          phone={listing.phone}
          callLabel={t("detail.call")}
          share={
            <ShareButton
              title={listing.name}
              url={businessLink(listing)}
              className="inline-flex h-12 min-w-0 items-center justify-center gap-2 rounded-[10px] border border-[#171717] px-3 text-[13px] font-bold text-[#171717] transition hover:bg-[#171717] hover:text-white dark:border-[#f2efe9] dark:text-[#f2efe9] dark:hover:bg-[#f2efe9] dark:hover:text-[#171614]"
            />
          }
        />
      )}

      {/* A service the guide has opened but nobody has claimed yet: the page's
            one job is to put it in front of whoever runs one. */}
      {placeholdersOnly && (
        <div className="mt-5 rounded-[12px] border border-[#171717] bg-[#FFF7ED] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#6d6d6a]">{t("detail.comingSoon")}</div>
          <p className="mt-2 text-[14px] font-medium leading-[1.6] text-[#171717]">{descriptionText}</p>
          <div className="mt-4">
            <ClaimBusinessButton listing={listing} me={me} />
          </div>
        </div>
      )}

      {!placeholdersOnly && (
        <div className="mt-4">
          <SocialBar listing={listing} lang={lang} />
        </div>
      )}

      {!placeholdersOnly && !hideMap && <DetailTabs items={navItems} label={es ? "Secciones del perfil" : "Business profile sections"} />}

      {/* LOOP is the sole fully built showcase profile; facts stay grounded in the listing and its official booking page. */}
      {!placeholdersOnly && isLoopShowcase && <LoopExperienceShowcase listing={listing} lang={lang} />}

      {/* Event feature belongs on the venue page as well as the landing-page carousel. */}
      {!placeholdersOnly && <KaraokeEventFeature listing={listing} />}

      {!placeholdersOnly && fareHarborUrl && (
        <section className="mt-5 rounded-[13px] border border-[#d8d6d0] bg-white p-4 dark:border-white/[0.12] dark:bg-[#252521]" aria-label={es ? "Reservación FareHarbor" : "FareHarbor booking"}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex min-h-9 items-center"><BookingPartnerLogo provider="fareharbor" className="!h-[22px]" /></span>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#69665f] dark:text-[#c2beb4]">{es ? "Reservación en esta página" : "On-site booking"}</span>
          </div>
          <h2 className="mt-2 text-[19px] font-extrabold tracking-tight">{es ? "Elige tu fecha y reserva" : "Choose your date and book"}</h2>
          <p className="mt-1.5 text-[13px] leading-relaxed text-[#65625c] dark:text-[#c2beb4]">{es ? "Consulta los horarios, precios y detalles actuales. La reservación y el pago se completan de forma segura mediante FareHarbor." : "Check current times, prices and activity details. Booking and payment are processed securely by FareHarbor."}</p>
          <a href={fareHarborUrl} className="mt-4 flex min-h-12 w-full items-center justify-center rounded-[10px] bg-[#171717] px-4 text-[13px] font-extrabold text-white transition-colors hover:bg-[#363633] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ef4036] dark:bg-white dark:text-black">{es ? "Consultar disponibilidad y reservar" : "Check availability & book"}</a>
          <p className="mt-2 text-[11px] text-[#817e78] dark:text-[#b9b5ad]">{es ? "Algunos dispositivos abren la página segura de FareHarbor." : "Some devices may open FareHarbor’s secure checkout page."}</p>
        </section>
      )}

      {!placeholdersOnly && (
        <div className={SECTION}>
          <DetailAbout
            heading={es ? "Sobre este negocio" : "About this business"}
            editorial={editorialDescriptionText}
            description={descriptionText}
            amenities={Array.isArray(listing.amenities) ? listing.amenities : []}
          />
        </div>
      )}

      {!placeholdersOnly && !hideMap && gallery.length >= 5 && (
        <BusinessPhotoStory photos={gallery} title={profileName} es={es} onOpen={setLightbox} />
      )}

      {!placeholdersOnly && hasHours && (
        <div className={SECTION}>
          <DetailHours listing={listing} />
        </div>
      )}

      {!placeholdersOnly && isRestaurant && (
        <section id="offerings" className={SECTION + " scroll-mt-28"}>
          <h2 className="text-[16px] font-extrabold tracking-[-0.01em] text-[#171717] dark:text-[#f2efe9]">
            {es ? "Menú y ofertas" : "Menu & offerings"}
          </h2>
          <p className="mt-2 text-[13px] leading-6 text-[#6d6d6a] dark:text-[#b8b3a9]">
            {es ? "Consulta el menú, haz un pedido o reserva directamente con el negocio." : "Open the business's own menu, ordering or booking page for the latest details."}
          </p>
          {menuUrl && (
            <button
              type="button"
              onClick={() => openExternal(menuUrl)}
              className="mt-3 inline-flex h-11 w-full items-center justify-center rounded-[10px] bg-[#171717] text-[12.5px] font-bold text-white transition hover:bg-[#33332f] dark:bg-[#f2efe9] dark:text-[#171614] dark:hover:bg-white"
            >
              {es ? "Ver menú completo" : "View full menu"}
            </button>
          )}
        </section>
      )}

      {!placeholdersOnly && (hasContact || mapNode) && (
        <div className={SECTION}>
          <DetailContact
            labels={{
              heading: es ? "Ubicación y contacto" : "Location & contact",
              address: t("detail.address"),
              phone: t("detail.phone"),
              website: es ? "Sitio web" : "Website",
              visitWebsite: es ? "Visitar sitio web" : "Visit website",
              area: es ? "Área" : "Area",
              languages: t("detail.languages"),
              viewMap: es ? "Ver en el mapa" : "View on map",
              googleMaps: "Google Maps",
              waze: "Waze",
            }}
            address={filled(listing.address) ? listing.address : ""}
            phone={filled(listing.phone) ? listing.phone : ""}
            website={withProtocol(listing.website_url)}
            area={areaText}
            languages={listing.language_support}
            directionsUrl={gmapsDir}
            wazeUrl={hasCoords ? wazeDir : ""}
            map={mapNode}
          />
        </div>
      )}

      {/* A beach carries the guide's own half as well: the feel of it, what the
            water does, what you can do in it and what's within walking distance. */}
      {isBeach && (
        <div className={SECTION}>
          <h2 className="text-[16px] font-extrabold tracking-[-0.01em] text-[#171717] dark:text-[#f2efe9]">{t("detail.beach")}</h2>
          {listing.beach_vibe && <p className="mt-2 text-[14px] font-bold text-[#171717] dark:text-[#f4f2ed]">{listing.beach_vibe}</p>}
          <div className="mt-2"><BeachCardDetails beach={beach} /></div>
        </div>
      )}

      {!placeholdersOnly && (
        <div className={SECTION}>
          <div className="mb-5 flex flex-col items-start justify-between gap-3 rounded-[14px] border border-[#eadfdd] bg-[#faf4f2] p-4 dark:border-white/[0.12] dark:bg-white/[0.06] sm:flex-row sm:items-center">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.13em] text-[#bc3440]">{es ? "Apoyo de la comunidad" : "Community love"}</p>
              <p className="mt-1 text-[13px] font-semibold leading-5 text-[#37332f] dark:text-[#ede5df]">{es ? "Sin estrellas ni puntuaciones. Cada corazón es un apoyo real, no una visita confirmada. ¡Dale amor a tus lugares favoritos!" : "No stars or low scores. Hearts are real endorsements, not verified visits. Show your favorite places some love."}</p>
            </div>
            <CommunityHeartButton listingId={listing.id} detail />
          </div>
          <DetailTips
            labels={{
              heading: es ? "Consejos de la comunidad" : "Community tips",
              count: `${reviews.length} ${es ? (reviews.length === 1 ? "tip" : "consejos") : (reviews.length === 1 ? "tip" : "tips")}`,
              empty: t("detail.noStories"),
              anonymous: es ? "Anónimo" : "Anonymous",
              share: es ? "Comparte un consejo" : "Share a tip",
              namePlaceholder: t("detail.yourName"),
              bodyPlaceholder: t("detail.tipPlaceholder"),
              submit: t("detail.submit"),
              submitting: t("detail.submitting"),
            }}
            reviews={reviews}
            form={form}
            setForm={setForm}
            onSubmit={submit}
            submitting={submitting}
            lang={lang}
          />
        </div>
      )}

      {/* The page's own hand-off: a visitor standing in the business holds up
            the code, the owner scans into their profile, and the same code then
            travels on to the next customer. */}
      {!placeholdersOnly && (
        <div className={SECTION}>
          <BusinessQrCard name={profileName} url={businessLink(listing)} />
        </div>
      )}

      {/* Owner claim — the page's last word, after the community has had its
            say, and the only claim on it: one quiet line, no plans, no price.
            A beach has nobody to claim it, and a coming-soon service already
            carries the claim in its notice at the top. */}
      {!isBeach && !placeholdersOnly && (
        <div className="mt-8 border-t border-[#e6e4df] pt-5 dark:border-white/[0.12]">
          <ClaimBusinessButton listing={listing} me={me} />
        </div>
      )}

      {!placeholdersOnly && (
        <div className="mt-6">
          <AdSlot placement="detail-strip" />
        </div>
      )}

      {isAdmin && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#d8d8d3] bg-white px-3 text-[12px] font-bold text-[#24231f] transition-colors hover:border-[#b84a3a]/50 hover:bg-[#f3f2ee] dark:border-white/[0.14] dark:bg-white/[0.05] dark:text-[#eeeae2] dark:hover:bg-white/[0.10]"
            aria-label={es ? "Editar listado" : "Edit listing"}
          >
            <Pencil className="h-4 w-4" />
            <span>{es ? "Editar" : "Edit"}</span>
          </button>
        </div>
      )}

      {/* Lightbox */}
      {lightbox != null && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90" onClick={() => setLightbox(null)}>
          {gallery.length > 1 && (
            <div className="absolute left-1/2 top-5 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 text-[12px] font-bold text-white">
              {lightbox + 1} / {gallery.length}
            </div>
          )}
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" aria-label="Close photo viewer">
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
            }}
            type="button"
            className="absolute left-3 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Previous photo">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <Image src={gallery[lightbox]} alt={`${profileName} photo ${lightbox + 1}`} fittingType="fit" className="max-h-[82dvh] max-w-[92vw] object-contain" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i + 1) % gallery.length);
            }}
            type="button"
            className="absolute right-3 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Next photo">
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}

      {editing && <ListingEditModal listing={listing} onClose={() => setEditing(false)} onSaved={setListing} />}

      {!placeholdersOnly && !hideMap && publicCta.url && (
        <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-black/[0.08] bg-white/94 px-4 pb-[calc(0.625rem+env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/[0.10] dark:bg-[#171816]/94 sm:px-6">
          <div className="mx-auto max-w-[520px]">
            <BookingActionTag type={BookingActionTag === "button" ? "button" : undefined} href={BookingActionTag === "a" ? fareHarborUrl : undefined} onClick={BookingActionTag === "button" ? () => openExternal(publicCta.url) : undefined} className="relative flex min-h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-[12px] border border-[#D90000] bg-gradient-to-b from-[#FF5C5C] via-[#EF0000] to-[#D90000] px-4 py-3 text-[12px] font-extrabold !text-white [text-shadow:0_1px_1px_rgba(80,0,0,0.35)] shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_3px_10px_rgba(239,0,0,0.2)] transition duration-200 hover:from-[#FF7777] hover:via-[#FF3333] hover:to-[#D90000] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717] focus-visible:ring-offset-2">
              <span className="pointer-events-none absolute inset-x-2 top-px h-px rounded-full bg-white/80" />
              <Calendar className="h-4 w-4 shrink-0" />
              <span>{bookingActionLabel}</span>
            </BookingActionTag>
          </div>
        </div>
      )}
    </div>);
}